import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MessageSquare, Loader2, ArrowRight, CheckCircle2 } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TOPICS = [
    { value: "general", label: "General inquiry" },
    { value: "support", label: "Account / billing support" },
    { value: "courtroom_99", label: "$99 Courtroom presale question" },
    { value: "firm_subscription", label: "Law-firm subscription question" },
    { value: "refund", label: "Refund request" },
    { value: "press", label: "Press / partnerships" },
    { value: "legal", label: "Legal / privacy / DMCA" },
];

export default function Contact() {
    const [form, setForm] = useState({
        email: "",
        name: "",
        topic: "general",
        message: "",
    });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!form.email.includes("@")) {
            toast.error("Enter a valid email");
            return;
        }
        if (form.message.trim().length < 10) {
            toast.error("Please add at least one sentence about your inquiry");
            return;
        }
        setBusy(true);
        try {
            await axios.post(`${API}/support/contact`, form);
            setDone(true);
            toast.success("Sent. We'll reply within 1 business day.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't send. Try emailing us directly.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-black text-white" data-testid="contact-page">
            <Header />
            <main className="max-w-[1400px] mx-auto px-6 sm:px-10 py-16">
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-16">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30] mb-3 flex items-center gap-2">
                            <MessageSquare size={11} /> CONTACT
                        </div>
                        <h1 className="font-anton text-5xl sm:text-6xl uppercase leading-[0.95] mb-6">
                            We Answer Every Email.
                        </h1>
                        <p className="font-sans text-lg text-white/75 leading-relaxed mb-10">
                            One human reads every message. No bots, no autoresponders pretending to
                            be people. We typically reply within 1 business day. Faster for paid
                            customers and active firms.
                        </p>

                        <div className="space-y-5 font-mono text-[11px] uppercase tracking-[0.2em]">
                            <div>
                                <div className="text-white/40 mb-1">GENERAL</div>
                                <a className="text-white hover:text-[#FF3B30] flex items-center gap-2" href="mailto:hello@alphacovenantholdings.com">
                                    <Mail size={11} /> hello@alphacovenantholdings.com
                                </a>
                            </div>
                            <div>
                                <div className="text-white/40 mb-1">REFUNDS &amp; BILLING</div>
                                <a className="text-white hover:text-[#FF3B30] flex items-center gap-2" href="mailto:hello@alphacovenantholdings.com">
                                    <Mail size={11} /> hello@alphacovenantholdings.com
                                </a>
                            </div>
                            <div>
                                <div className="text-white/40 mb-1">LEGAL / PRIVACY</div>
                                <a className="text-white hover:text-[#FF3B30] flex items-center gap-2" href="mailto:hello@alphacovenantholdings.com">
                                    <Mail size={11} /> hello@alphacovenantholdings.com
                                </a>
                            </div>
                            <div>
                                <div className="text-white/40 mb-1">SECURITY</div>
                                <a className="text-white hover:text-[#FF3B30] flex items-center gap-2" href="mailto:hello@alphacovenantholdings.com">
                                    <Mail size={11} /> hello@alphacovenantholdings.com
                                </a>
                            </div>
                            <div className="pt-4 border-t border-white/10">
                                <div className="text-white/40 mb-1">SUPPORT HOURS</div>
                                <div className="text-white/70 normal-case tracking-normal font-sans text-sm">
                                    Monday – Friday<br />
                                    9:00 AM – 6:00 PM ET<br />
                                    Reply within 1 business day
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="border-2 border-[#FF3B30] bg-[#FF3B30]/5 p-8" data-testid="contact-form">
                        {done ? (
                            <div data-testid="contact-success">
                                <div className="flex items-center gap-2 mb-3">
                                    <CheckCircle2 size={20} className="text-[#00FF85]" />
                                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00FF85]">
                                        SENT
                                    </div>
                                </div>
                                <div className="font-anton text-2xl uppercase mb-3">
                                    We&apos;ll reply within 1 business day.
                                </div>
                                <p className="font-sans text-sm text-white/75 leading-relaxed">
                                    Check your inbox (and spam folder, just in case). If it&apos;s
                                    urgent, email us directly at
                                    {" "}<a className="text-white underline" href="mailto:hello@alphacovenantholdings.com">hello@alphacovenantholdings.com</a>.
                                </p>
                            </div>
                        ) : (
                            <form onSubmit={submit}>
                                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30] mb-3">
                                    SEND A MESSAGE
                                </div>
                                <div className="font-anton text-2xl uppercase mb-5">
                                    Tell us what you need.
                                </div>
                                <input
                                    required
                                    type="email"
                                    placeholder="YOUR EMAIL"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    className="w-full bg-black border-2 border-white/30 p-3 mb-2 font-mono text-xs text-white placeholder-white/40 focus:border-[#FF3B30] outline-none"
                                    data-testid="contact-email"
                                />
                                <input
                                    type="text"
                                    placeholder="YOUR NAME (OPTIONAL)"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    className="w-full bg-black border-2 border-white/30 p-3 mb-2 font-mono text-xs text-white placeholder-white/40 focus:border-[#FF3B30] outline-none"
                                    data-testid="contact-name"
                                />
                                <select
                                    value={form.topic}
                                    onChange={(e) => setForm({ ...form, topic: e.target.value })}
                                    className="w-full bg-black border-2 border-white/30 p-3 mb-2 font-mono text-xs text-white focus:border-[#FF3B30] outline-none"
                                    data-testid="contact-topic"
                                >
                                    {TOPICS.map((t) => (
                                        <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                </select>
                                <textarea
                                    required
                                    placeholder="HOW CAN WE HELP?"
                                    rows={6}
                                    value={form.message}
                                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                                    className="w-full bg-black border-2 border-white/30 p-3 mb-4 font-mono text-xs text-white placeholder-white/40 focus:border-[#FF3B30] outline-none resize-none"
                                    data-testid="contact-message"
                                />
                                <button
                                    type="submit"
                                    disabled={busy}
                                    className="btn-brutal btn-brutal-red w-full justify-center text-[10px]"
                                    data-testid="contact-submit"
                                >
                                    {busy ? (
                                        <Loader2 size={12} className="animate-spin" />
                                    ) : (
                                        <>SEND MESSAGE <ArrowRight size={12} /></>
                                    )}
                                </button>
                                <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/40 mt-3 text-center">
                                    BY SUBMITTING YOU AGREE TO OUR <a className="underline" href="/privacy">PRIVACY POLICY</a>
                                </p>
                            </form>
                        )}
                    </div>
                </div>
            </main>
            <Footer disclaimer="HPM provides legal information, not legal advice. Consult a licensed attorney for legal advice specific to your situation." />
        </div>
    );
}
